import { memo, useEffect, useMemo, useState } from 'react'
import { useSelector, useStore } from 'react-redux'
import { getSecurityFromCache, getSecurityKey, isFnOSecurity } from '../../../util/security'
import { selectIsPrimarySecurityLoading, selectPrimarySecurity } from '../../../selectors/home'
import { ViewType } from '../../../assets/constants'
import { useStyles } from './styles'
import { matchPath, useLocation } from 'react-router-dom'
import ROUTES from '../../../assets/routes'
import LiveMarketText from '../../../components/common/LiveMarketText'
import LiveFlashValue from '../../../components/common/LiveFlashValue'
import { RootState } from '../../../store'
import {
    buildLtpViewLiveSelectors,
    hasLtpViewLiveData,
    LtpViewLiveConfig,
    shouldShowLtpViewChange,
} from './ltpViewLiveDisplay'

/**
 * Reserved slot: the widest common case (NIFTY / SENSEX style prices). The
 * hidden sizer renders this in the live styles, so the bar keeps this much
 * room whether the placeholder, the price alone, or price + change is shown.
 */
const RESERVED_LTP = '72,771.72'
const RESERVED_CHANGE = '-1124.02 (-1.52%)'
const RESERVED_CHANGE_NARROW = '-1.52%'

const ltpStyle = { fontSize: '1rem', marginLeft: '4px' } as const
const inrStyle = { marginLeft: '4px', fontSize: '10px' } as const
const changeStyle = { fontSize: '14px', textAlign: 'right', marginLeft: '8px' } as const

/** Same markup as the live row so text metrics (and therefore the slot size) match exactly. */
const LtpRowSample = ({ className, narrowWidth }: { className?: string; narrowWidth: boolean }) => (
    <>
        <span className={className} style={ltpStyle}>
            {RESERVED_LTP}
        </span>
        <span className={className ? `inr-tag ${className}` : 'inr-tag'} style={inrStyle}>
            INR
        </span>
        <span className={className} style={changeStyle}>
            {narrowWidth ? RESERVED_CHANGE_NARROW : RESERVED_CHANGE}
        </span>
    </>
)

const LTPView = memo(({ width, view }: { width: number | undefined; view: ViewType }) => {
    const { classes } = useStyles()
    const location = useLocation()
    const store = useStore<RootState>()
    const { key, expressionMode, primary, underlying, expressionSecurities, underlyingSecurityCode } =
        useSelector(selectPrimarySecurity)
    const isPrimarySecurityLoading = useSelector(selectIsPrimarySecurityLoading)

    const isSimulator = useMemo(() => Boolean(matchPath(ROUTES.simulator, location.pathname)), [location.pathname])
    const narrowWidth = !!(width && width <= 800)

    const isDirectPrimaryView =
        view === ViewType.CHART ||
        view === ViewType.SYMBOL ||
        view === ViewType.FINANCIAL_DATA ||
        view === ViewType.SEASONALS ||
        view === ViewType.TECHNICALS ||
        view === ViewType.INDEX_COMPONENTS ||
        view === ViewType.SHAREHOLDER_HOLDINGS_DATA

    const selectedSecurity = useMemo(
        () =>
            expressionMode
                ? null
                : primary && isFnOSecurity(primary) && !isDirectPrimaryView
                  ? underlying
                  : primary,
        [expressionMode, isDirectPrimaryView, primary, underlying]
    )

    const displayKey = useMemo(() => {
        if (expressionMode || !key) return ''
        if (selectedSecurity) return getSecurityKey(selectedSecurity.exchange, selectedSecurity.code)
        const useUnderlyingKey = !primary && !!underlyingSecurityCode && !isDirectPrimaryView
        return useUnderlyingKey ? underlyingSecurityCode : key
    }, [expressionMode, isDirectPrimaryView, selectedSecurity, key, primary, underlyingSecurityCode])

    const securityForStats = useMemo(
        () => selectedSecurity ?? (displayKey ? getSecurityFromCache(displayKey) : undefined),
        [selectedSecurity, displayKey]
    )

    const simulatorUnderlyingKey = useMemo(
        () => getSecurityKey(selectedSecurity?.exchange ?? 0, selectedSecurity?.code ?? ''),
        [selectedSecurity]
    )

    const liveConfig = useMemo((): LtpViewLiveConfig | null => {
        if (!key) return null
        return {
            displayKey,
            expressionKey: key,
            expressionMode,
            isSimulator,
            simulatorUnderlyingKey,
            security: securityForStats,
            expressionSecurities: expressionSecurities ?? [],
            narrowWidth,
        }
    }, [
        key,
        displayKey,
        expressionMode,
        isSimulator,
        simulatorUnderlyingKey,
        securityForStats,
        expressionSecurities,
        narrowWidth,
    ])

    const liveSelectors = useMemo(
        () => (liveConfig ? buildLtpViewLiveSelectors(liveConfig) : null),
        [liveConfig]
    )

    const [dataReady, setDataReady] = useState(() =>
        liveConfig ? hasLtpViewLiveData(store.getState(), liveConfig) : false
    )
    const [showChange, setShowChange] = useState(() =>
        liveConfig ? shouldShowLtpViewChange(store.getState(), liveConfig) : false
    )

    useEffect(() => {
        if (!liveConfig) {
            setDataReady(false)
            setShowChange(false)
            return
        }
        const state = store.getState()
        setDataReady(hasLtpViewLiveData(state, liveConfig))
        setShowChange(shouldShowLtpViewChange(state, liveConfig))
    }, [liveConfig, store])

    useEffect(() => {
        if (!liveConfig || (dataReady && showChange)) return
        return store.subscribe(() => {
            const state = store.getState()
            if (!dataReady && hasLtpViewLiveData(state, liveConfig)) {
                setDataReady(true)
            }
            if (!showChange && shouldShowLtpViewChange(state, liveConfig)) {
                setShowChange(true)
            }
        })
    }, [liveConfig, dataReady, showChange, store])

    const showShimmer =
        !key ||
        (isPrimarySecurityLoading && !securityForStats) ||
        (!!key && !dataReady)

    return (
        <div className={classes.container}>
            <div className={`security-ltp font-feature-num ${classes.ltp}`}>
                <span className={classes.sizer} aria-hidden="true">
                    <LtpRowSample narrowWidth={narrowWidth} />
                </span>
                {showShimmer || !liveSelectors ? (
                    <span className={classes.slot} aria-busy="true" aria-label="Loading price">
                        <LtpRowSample className={classes.placeholder} narrowWidth={narrowWidth} />
                    </span>
                ) : (
                    <span className={classes.slot}>
                        <LiveFlashValue selectText={liveSelectors.ltp} style={ltpStyle} />
                        <span className="inr-tag" style={inrStyle}>
                            INR
                        </span>
                        {showChange && (
                            <LiveMarketText
                                selectText={liveSelectors.change}
                                selectColor={liveSelectors.changeColor}
                                style={changeStyle}
                            />
                        )}
                    </span>
                )}
            </div>
        </div>
    )
})
LTPView.displayName = 'LtpView'
export default LTPView
